import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import adminService from '../../services/adminService';

const AdminDashboard = ({ onTabChange }) => {
  const { currentUser, getProfile } = useAuth();
  const [stats, setStats] = useState(null);
  const [recentActivity, setRecentActivity] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [showImageModal, setShowImageModal] = useState(false);
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [selectedImage, setSelectedImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);

  useEffect(() => {
    const loadDashboardData = async () => {
      try {
        const [statsResponse, activityResponse] = await Promise.all([
          adminService.getDashboardStats(),
          adminService.getRecentActivity()
        ]);
        setStats(statsResponse.data);
        setRecentActivity(activityResponse.data || []);
      } catch (error) {
        setError('Erreur lors du chargement des données');
        console.error('Erreur dashboard:', error);
      } finally {
        setLoading(false);
      }
    };

    // Charger aussi le profil utilisateur pour s'assurer d'avoir les dernières données
    const loadProfile = async () => {
      try {
        await getProfile();
      } catch (error) {
        console.error('Erreur chargement profil:', error);
      }
    };

    loadDashboardData();
    loadProfile();
    
    // Refresh des données toutes les 30 secondes
    const interval = setInterval(loadDashboardData, 30000);
    return () => clearInterval(interval);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      alert('Les mots de passe ne correspondent pas');
      return;
    }
    try {
      await adminService.changePassword(passwordData);
      alert('Mot de passe modifié avec succès');
      setShowPasswordModal(false);
      setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
    } catch (error) {
      alert('Erreur lors du changement de mot de passe: ' + error.message);
    }
  };

  const handleImageUpload = async (e) => {
    e.preventDefault();
    if (!selectedImage) {
      alert('Veuillez sélectionner une image');
      return;
    }
    try {
      const formData = new FormData();
      formData.append('profileImage', selectedImage);
      await adminService.updateProfileImage(formData);
      
      // Rafraîchir les données utilisateur pour récupérer l'image mise à jour
      await getProfile();
      
      alert('Image de profil mise à jour avec succès');
      setShowImageModal(false);
      setSelectedImage(null);
      setImagePreview(null);
      
      // Forcer un refresh de la page pour s'assurer que l'image s'affiche
      window.location.reload();
    } catch (error) {
      alert('Erreur lors de la mise à jour de l\'image: ' + error.message);
    }
  };

  const handleImageSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      setSelectedImage(file);
      const reader = new FileReader();
      reader.onload = (e) => setImagePreview(e.target.result);
      reader.readAsDataURL(file);
    }
  };

  const handleStatCardClick = (statType) => {
    const modelMapping = {
      'hotels': 'hotels',
      'circuits': 'circuits',
      'transports': 'transports',
      'visites': 'visites',
      'voitures': 'voitures',
      'comptes': 'comptes',
      'reservationHotels': 'reservationHotels',
      'reservationCircuits': 'reservationCircuits',
      'reservationTransports': 'reservationTransports',
      'reservationVisites': 'reservationVisites',
      'reservationVoitures': 'reservationVoitures'
    };
    
    if (modelMapping[statType] && onTabChange) {
      onTabChange(modelMapping[statType]);
    }
  };

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow-sm p-6">
        <div className="animate-pulse">
          <div className="h-8 bg-slate-200 rounded w-1/4 mb-6"></div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[...Array(8)].map((_, i) => (
              <div key={i} className="h-24 bg-slate-200 rounded"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white rounded-lg shadow-sm p-6">
        <div className="text-center">
          <div className="text-red-500 text-lg mb-2">⚠️</div>
          <p className="text-red-600">{error}</p>
        </div>
      </div>
    );
  }

  const statCards = [
    {
      title: 'Total Réservations',
      value: stats?.totalReservations || 0,
      icon: '📋',
      color: 'bg-blue-500',
      textColor: 'text-blue-600',
      key: 'totalReservations'
    },
    {
      title: 'Hôtels',
      value: stats?.hotels || 0,
      icon: '🏨',
      color: 'bg-green-500',
      textColor: 'text-green-600',
      key: 'hotels'
    },
    {
      title: 'Circuits',
      value: stats?.circuits || 0,
      icon: '🗺️',
      color: 'bg-purple-500',
      textColor: 'text-purple-600',
      key: 'circuits'
    },
    {
      title: 'Transports',
      value: stats?.transports || 0,
      icon: '🚌',
      color: 'bg-yellow-500',
      textColor: 'text-yellow-600',
      key: 'transports'
    },
    {
      title: 'Visites Guidées',
      value: stats?.visites || 0,
      icon: '👁️',
      color: 'bg-indigo-500',
      textColor: 'text-indigo-600',
      key: 'visites'
    },
    {
      title: 'Voitures de Location',
      value: stats?.voitures || 0,
      icon: '🚗',
      color: 'bg-red-500',
      textColor: 'text-red-600',
      key: 'voitures'
    },
    {
      title: 'Comptes Utilisateurs',
      value: stats?.comptes || 0,
      icon: '👥',
      color: 'bg-pink-500',
      textColor: 'text-pink-600',
      key: 'comptes'
    },
    {
      title: 'Réservations Hôtels',
      value: stats?.reservationHotels || 0,
      icon: '🏨',
      color: 'bg-teal-500',
      textColor: 'text-teal-600',
      key: 'reservationHotels'
    }
  ];

  const reservationStats = [
    {
      title: 'Réservations Circuits',
      value: stats?.reservationCircuits || 0,
      icon: '🗺️',
      key: 'reservationCircuits'
    },
    {
      title: 'Réservations Transports',
      value: stats?.reservationTransports || 0,
      icon: '🚌',
      key: 'reservationTransports'
    },
    {
      title: 'Réservations Visites',
      value: stats?.reservationVisites || 0,
      icon: '👁️',
      key: 'reservationVisites'
    },
    {
      title: 'Réservations Voitures',
      value: stats?.reservationVoitures || 0,
      icon: '🚗',
      key: 'reservationVoitures'
    }
  ];

  // ===== Icônes SVG (stroke, currentColor) =====
  const SVGIcon = ({ name, className = "w-6 h-6" }) => {
    const paths = {
      clipboard: <path d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />,
      building: <path d="M3 21h18M6 21V5a1 1 0 011-1h10a1 1 0 011 1v16M9 8h.01M13 8h.01M9 12h.01M13 12h.01M9 16h.01M13 16h.01" />,
      map: <path d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7" />,
      bus: <><rect x="4" y="5" width="16" height="11" rx="2" /><path d="M4 11h16M8 5v6m8-6v6M7 19v1m10-1v1M8 16h.01M16 16h.01" /></>,
      eye: <><path d="M2.5 12S6 5 12 5s9.5 7 9.5 7-3.5 7-9.5 7-9.5-7-9.5-7z" /><circle cx="12" cy="12" r="3" /></>,
      car: <><path d="M5 11l1.6-4.3A2 2 0 018.5 5.4h7a2 2 0 011.9 1.3L19 11M4 11h16v5a1 1 0 01-1 1h-1m-12 0H5a1 1 0 01-1-1v-5" /><circle cx="7.5" cy="16.5" r="1.5" /><circle cx="16.5" cy="16.5" r="1.5" /></>,
      users: <path d="M17 20h5v-2a4 4 0 00-3-3.87M9 20H2v-2a4 4 0 013-3.87m11-2a4 4 0 10-4-4 4 4 0 004 4zm-7 0a3 3 0 100-6 3 3 0 000 6z" />,
      calendar: <><rect x="4" y="5" width="16" height="16" rx="2" /><path d="M8 3v4m8-4v4M4 10h16M9 15l2 2 4-4" /></>,
      bolt: <path d="M13 2L4.5 13.5H11l-1 8.5L19.5 10H13l0-8z" />,
      clock: <><circle cx="12" cy="12" r="9" /><path d="M12 8v4l3 2" /></>,
      chart: <path d="M4 19V5m0 14h16M8 17V11m4 6V7m4 10v-4" />,
    };
    return (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}>
        {paths[name] || paths.chart}
      </svg>
    );
  };

  // ===== Design (dégradé + icône) par clé de stat =====
  const META = {
    totalReservations: { grad: "from-violet-500 to-purple-600", ring: "violet", icon: "clipboard" },
    hotels: { grad: "from-sky-500 to-blue-600", ring: "sky", icon: "building" },
    circuits: { grad: "from-fuchsia-500 to-purple-600", ring: "fuchsia", icon: "map" },
    transports: { grad: "from-amber-500 to-orange-600", ring: "amber", icon: "bus" },
    visites: { grad: "from-emerald-500 to-teal-600", ring: "emerald", icon: "eye" },
    voitures: { grad: "from-rose-500 to-red-600", ring: "rose", icon: "car" },
    comptes: { grad: "from-pink-500 to-rose-600", ring: "pink", icon: "users" },
    reservationHotels: { grad: "from-teal-500 to-cyan-600", ring: "teal", icon: "calendar" },
    reservationCircuits: { grad: "from-indigo-500 to-violet-600", ring: "indigo", icon: "map" },
    reservationTransports: { grad: "from-orange-500 to-amber-600", ring: "orange", icon: "bus" },
    reservationVisites: { grad: "from-green-500 to-emerald-600", ring: "green", icon: "eye" },
    reservationVoitures: { grad: "from-red-500 to-rose-600", ring: "red", icon: "car" },
  };
  const metaFor = (k) => META[k] || { grad: "from-slate-500 to-slate-700", icon: "chart" };

  return (
    <div className="space-y-6 lg:space-y-7">
      {/* ===== Hero header premium ===== */}
      <div className="relative overflow-hidden rounded-3xl shadow-xl">
        <div className="absolute inset-0 bg-gradient-to-br from-slate-900 via-slate-800 to-orange-700" />
        <div className="absolute inset-0 opacity-30" style={{ backgroundImage: "radial-gradient(circle at 20% 20%, rgba(255,255,255,.15), transparent 40%), radial-gradient(circle at 90% 80%, rgba(231,198,90,.25), transparent 45%)" }} />
        <div className="relative px-6 py-7 lg:px-8 lg:py-8 flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6">
          <div className="flex items-center gap-4">
            <div className="relative">
              {currentUser?.profileImage ? (
                <img
                  src={`https://nouzhaback-theta.vercel.app${currentUser.profileImage}`}
                  alt="Profile"
                  className="w-20 h-20 rounded-2xl object-cover ring-4 ring-white/20 shadow-lg"
                  onError={(e) => { e.target.style.display = 'none'; if (e.target.nextSibling?.style) e.target.nextSibling.style.display = 'flex'; }}
                  onLoad={(e) => { if (e.target.nextSibling?.style) e.target.nextSibling.style.display = 'none'; }}
                />
              ) : null}
              <div className={`w-20 h-20 rounded-2xl bg-gradient-to-br from-orange-400 to-rose-500 flex items-center justify-center text-3xl text-white font-black ring-4 ring-white/20 shadow-lg ${currentUser?.profileImage ? 'hidden' : ''}`}>
                {currentUser?.username?.charAt(0)?.toUpperCase() || 'A'}
              </div>
              <button
                onClick={() => setShowImageModal(true)}
                aria-label="Changer la photo"
                className="absolute -bottom-2 -right-2 w-8 h-8 bg-white text-slate-700 rounded-xl flex items-center justify-center shadow-lg hover:scale-110 transition-transform"
              >
                📷
              </button>
            </div>
            <div className="text-white">
              <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-amber-300/90">Espace administrateur</p>
              <h2 className="text-2xl lg:text-3xl font-black mt-1">Bonjour, {currentUser?.username || 'Admin'} 👋</h2>
              <p className="text-white/70 text-sm mt-0.5">{currentUser?.email || 'admin@palace.com'}</p>
              <div className="flex flex-wrap items-center gap-2 mt-3">
                <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-bold bg-emerald-400/20 text-emerald-200 border border-emerald-300/30">✅ Compte vérifié</span>
                <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-bold bg-white/10 text-white/80 border border-white/15">👑 Administrateur</span>
              </div>
            </div>
          </div>
          <div className="flex flex-col items-stretch lg:items-end gap-3">
            <button
              onClick={() => setShowPasswordModal(true)}
              className="inline-flex items-center justify-center gap-2 px-5 py-3 bg-white text-slate-800 rounded-2xl font-bold text-sm shadow-lg hover:bg-amber-50 hover:scale-[1.03] transition-all"
            >
              🔐 Changer le mot de passe
            </button>
            <div className="text-xs text-white/60 lg:text-right">
              🕒 {new Date().toLocaleString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long', hour: '2-digit', minute: '2-digit' })}
            </div>
          </div>
        </div>
      </div>

      {/* ===== Statistiques principales ===== */}
      <div>
        <h3 className="text-xs font-bold uppercase tracking-[0.14em] text-slate-400 mb-3 px-1">Vue d'ensemble</h3>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 lg:gap-5">
          {statCards.map((stat, index) => {
            const m = metaFor(stat.key);
            return (
              <button
                key={index}
                onClick={() => handleStatCardClick(stat.key)}
                className="group relative text-left bg-white rounded-2xl p-4 lg:p-5 border border-slate-100 shadow-sm hover:shadow-xl hover:-translate-y-1 transition-all duration-300 overflow-hidden"
              >
                <div className={`absolute -right-6 -top-6 w-24 h-24 rounded-full bg-gradient-to-br ${m.grad} opacity-[0.07] group-hover:opacity-15 group-hover:scale-125 transition-all duration-500`} />
                <div className="relative flex items-start justify-between">
                  <div className={`w-11 h-11 rounded-xl bg-gradient-to-br ${m.grad} text-white flex items-center justify-center shadow-md group-hover:scale-110 transition-transform`}>
                    <SVGIcon name={m.icon} className="w-6 h-6" />
                  </div>
                  <span className="text-slate-300 group-hover:text-slate-500 transition-colors text-lg">↗</span>
                </div>
                <p className="relative mt-3 text-2xl lg:text-3xl font-black text-slate-800 tabular-nums">{stat.value.toLocaleString()}</p>
                <p className="relative text-xs lg:text-sm font-medium text-slate-500 truncate">{stat.title}</p>
              </button>
            );
          })}
        </div>
      </div>

      {/* ===== Détail des réservations ===== */}
      <div className="bg-white rounded-3xl shadow-sm p-5 lg:p-6 border border-slate-100">
        <h3 className="text-base lg:text-lg font-black text-slate-800 mb-5 flex items-center gap-2">
          <span className="w-8 h-8 rounded-xl bg-gradient-to-br from-violet-500 to-purple-600 text-white flex items-center justify-center"><SVGIcon name="chart" className="w-5 h-5" /></span>
          Détail des Réservations
        </h3>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 lg:gap-4">
          {reservationStats.map((stat, index) => {
            const m = metaFor(stat.key);
            return (
              <button
                key={index}
                onClick={() => handleStatCardClick(stat.key)}
                className="group flex items-center gap-3 p-3.5 rounded-2xl bg-slate-50 border border-slate-100 hover:bg-white hover:border-slate-200 hover:shadow-md transition-all text-left"
              >
                <span className={`w-10 h-10 flex-none rounded-xl bg-gradient-to-br ${m.grad} text-white flex items-center justify-center shadow-sm group-hover:scale-110 transition-transform`}>
                  <SVGIcon name={m.icon} className="w-5 h-5" />
                </span>
                <div className="min-w-0">
                  <p className="text-xl font-black text-slate-800 tabular-nums leading-none">{stat.value.toLocaleString()}</p>
                  <p className="text-[11px] lg:text-xs text-slate-500 truncate mt-1">{stat.title}</p>
                </div>
              </button>
            );
          })}
        </div>
      </div>

      {/* ===== Actions rapides ===== */}
      <div className="bg-white rounded-3xl shadow-sm p-5 lg:p-6 border border-slate-100">
        <h3 className="text-base lg:text-lg font-black text-slate-800 mb-5 flex items-center gap-2">
          <span className="w-8 h-8 rounded-xl bg-gradient-to-br from-amber-500 to-orange-600 text-white flex items-center justify-center"><SVGIcon name="bolt" className="w-5 h-5" /></span>
          Actions Rapides
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3 lg:gap-4">
          {[
            { key: 'hotels', icon: 'building', grad: 'from-sky-500 to-blue-600', title: 'Gérer les Hôtels', sub: 'Ajouter, modifier' },
            { key: 'circuits', icon: 'map', grad: 'from-fuchsia-500 to-purple-600', title: 'Gérer les Circuits', sub: 'Programmes' },
            { key: 'voitures', icon: 'car', grad: 'from-rose-500 to-red-600', title: 'Gérer les Voitures', sub: 'Flotte de location' },
            { key: 'reservationHotels', icon: 'clipboard', grad: 'from-amber-500 to-orange-600', title: 'Réservations', sub: 'Suivi des commandes' },
          ].map((a) => (
            <button
              key={a.key}
              onClick={() => handleStatCardClick(a.key)}
              className="group flex items-center gap-3 p-4 rounded-2xl bg-slate-50 border border-slate-100 hover:bg-white hover:shadow-lg hover:-translate-y-0.5 transition-all text-left"
            >
              <span className={`w-11 h-11 flex-none rounded-xl bg-gradient-to-br ${a.grad} text-white flex items-center justify-center shadow-md group-hover:scale-110 transition-transform`}>
                <SVGIcon name={a.icon} className="w-6 h-6" />
              </span>
              <div className="min-w-0">
                <p className="font-bold text-slate-800 text-sm truncate">{a.title}</p>
                <p className="text-xs text-slate-500 truncate">{a.sub}</p>
              </div>
            </button>
          ))}
        </div>
      </div>

      {/* ===== Activité récente ===== */}
      <div className="bg-white rounded-3xl shadow-sm p-5 lg:p-6 border border-slate-100">
        <h3 className="text-base lg:text-lg font-black text-slate-800 mb-5 flex items-center gap-2">
          <span className="w-8 h-8 rounded-xl bg-gradient-to-br from-teal-500 to-cyan-600 text-white flex items-center justify-center"><SVGIcon name="clock" className="w-5 h-5" /></span>
          Activité Récente
        </h3>
        <div className="space-y-1">
          {(recentActivity.length > 0 ? recentActivity : [
            { icon: '✅', description: 'Système initialisé', timestamp: 'Il y a quelques instants' },
            { icon: '📊', description: 'Tableau de bord chargé', timestamp: 'Maintenant' },
          ]).map((activity, index, arr) => (
            <div key={index} className="flex items-start gap-3 group">
              <div className="flex flex-col items-center">
                <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-emerald-400 to-teal-500 text-white flex items-center justify-center text-sm shadow-sm flex-none">
                  {activity.icon || '✅'}
                </div>
                {index < arr.length - 1 && <div className="w-px flex-1 bg-slate-200 my-1" />}
              </div>
              <div className="flex-1 min-w-0 pb-4 pt-1.5">
                <p className="text-sm font-semibold text-slate-800 truncate group-hover:text-orange-600 transition-colors">{activity.description}</p>
                <p className="text-xs text-slate-400 mt-0.5">{activity.timestamp}</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Modal changement de mot de passe */}
      {showPasswordModal && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-[70] p-4 pt-20">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md">
            <div className="bg-gradient-to-r from-orange-500 via-red-500 to-pink-500 px-6 py-4 rounded-t-2xl">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-white/20 rounded-xl backdrop-blur-sm">
                    <span className="text-xl">🔐</span>
                  </div>
                  <h3 className="text-xl font-bold text-white">Changer le mot de passe</h3>
                </div>
                <button
                  onClick={() => setShowPasswordModal(false)}
                  className="p-2 bg-white/20 hover:bg-white/30 rounded-xl backdrop-blur-sm transition-all duration-300"
                >
                  <span className="text-white text-lg">❌</span>
                </button>
              </div>
            </div>
            
            <form onSubmit={handlePasswordChange} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  🔑 Mot de passe actuel
                </label>
                <input
                  type="password"
                  required
                  value={passwordData.currentPassword}
                  onChange={(e) => setPasswordData({...passwordData, currentPassword: e.target.value})}
                  className="w-full px-4 py-3 border border-slate-300 rounded-xl focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                  placeholder="Saisir le mot de passe actuel"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  🆕 Nouveau mot de passe
                </label>
                <input
                  type="password"
                  required
                  value={passwordData.newPassword}
                  onChange={(e) => setPasswordData({...passwordData, newPassword: e.target.value})}
                  className="w-full px-4 py-3 border border-slate-300 rounded-xl focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                  placeholder="Saisir le nouveau mot de passe"
                  minLength="6"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  ✅ Confirmer le nouveau mot de passe
                </label>
                <input
                  type="password"
                  required
                  value={passwordData.confirmPassword}
                  onChange={(e) => setPasswordData({...passwordData, confirmPassword: e.target.value})}
                  className="w-full px-4 py-3 border border-slate-300 rounded-xl focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                  placeholder="Confirmer le nouveau mot de passe"
                  minLength="6"
                />
              </div>
              
              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowPasswordModal(false)}
                  className="flex-1 px-4 py-3 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-xl font-medium transition-all duration-300"
                >
                  Annuler
                </button>
                <button
                  type="submit"
                  className="flex-1 px-4 py-3 bg-gradient-to-r from-orange-500 via-red-500 to-pink-500 hover:from-orange-600 hover:via-red-600 hover:to-pink-600 text-white rounded-xl font-medium transition-all duration-300"
                >
                  Confirmer
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal upload d'image */}
      {showImageModal && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-[70] p-4 pt-20">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md">
            <div className="bg-gradient-to-r from-orange-500 via-red-500 to-pink-500 px-6 py-4 rounded-t-2xl">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-white/20 rounded-xl backdrop-blur-sm">
                    <span className="text-xl">📷</span>
                  </div>
                  <h3 className="text-xl font-bold text-white">Changer l'image de profil</h3>
                </div>
                <button
                  onClick={() => setShowImageModal(false)}
                  className="p-2 bg-white/20 hover:bg-white/30 rounded-xl backdrop-blur-sm transition-all duration-300"
                >
                  <span className="text-white text-lg">❌</span>
                </button>
              </div>
            </div>
            
            <form onSubmit={handleImageUpload} className="p-6 space-y-4">
              <div className="text-center">
                {imagePreview ? (
                  <div className="relative inline-block">
                    <img
                      src={imagePreview}
                      alt="Aperçu"
                      className="w-32 h-32 rounded-full object-cover border-4 border-orange-200"
                    />
                    <button
                      type="button"
                      onClick={() => {setImagePreview(null); setSelectedImage(null);}}
                      className="absolute -top-2 -right-2 w-8 h-8 bg-red-500 rounded-full flex items-center justify-center text-white text-sm hover:bg-red-600 transition-colors"
                    >
                      ❌
                    </button>
                  </div>
                ) : (
                  <div className="w-32 h-32 mx-auto bg-gradient-to-br from-orange-400 to-red-400 rounded-full flex items-center justify-center text-white text-4xl font-bold">
                    {currentUser?.username?.charAt(0)?.toUpperCase() || 'A'}
                  </div>
                )}
              </div>
              
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  🖼️ Sélectionner une nouvelle image
                </label>
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleImageSelect}
                  className="w-full px-4 py-3 border border-slate-300 rounded-xl focus:ring-2 focus:ring-orange-500 focus:border-transparent file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-medium file:bg-orange-50 file:text-orange-700 hover:file:bg-orange-100"
                />
              </div>
              
              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowImageModal(false)}
                  className="flex-1 px-4 py-3 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-xl font-medium transition-all duration-300"
                >
                  Annuler
                </button>
                <button
                  type="submit"
                  className="flex-1 px-4 py-3 bg-gradient-to-r from-orange-500 via-red-500 to-pink-500 hover:from-orange-600 hover:via-red-600 hover:to-pink-600 text-white rounded-xl font-medium transition-all duration-300"
                >
                  Sauvegarder
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminDashboard;
